import { useCallback, useEffect, useRef, useState } from "react";

import {
  DashboardLayout,
  copyDashboardLayout,
  dashboardLayoutsEqual,
  readDashboardLayout,
  writeDashboardLayout,
} from "./dashboardLayout";

export interface DashboardLayoutHistory<WidgetType extends string> {
  future: ReadonlyArray<DashboardLayout<WidgetType>>;
  past: ReadonlyArray<DashboardLayout<WidgetType>>;
}

interface UseDashboardEditorOptions<WidgetType extends string> {
  defaultLayout: DashboardLayout<WidgetType>;
  isValidLayout: (value: unknown) => value is DashboardLayout<WidgetType>;
  storageKey: string | null;
}

export function useDashboardEditor<WidgetType extends string>({
  defaultLayout,
  isValidLayout,
  storageKey,
}: UseDashboardEditorOptions<WidgetType>) {
  const [savedLayout, setSavedLayout] = useState(() =>
    copyDashboardLayout(defaultLayout),
  );
  const [draftLayout, setDraftLayout] = useState(() =>
    copyDashboardLayout(defaultLayout),
  );
  const [layoutHistory, setLayoutHistory] = useState<
    DashboardLayoutHistory<WidgetType>
  >({ future: [], past: [] });
  const draftLayoutRef = useRef(draftLayout);

  const replaceDraftLayout = useCallback(
    (layout: DashboardLayout<WidgetType>) => {
      const nextLayout = copyDashboardLayout(layout);
      draftLayoutRef.current = nextLayout;
      setDraftLayout(nextLayout);
    },
    [],
  );

  const clearHistory = useCallback(() => {
    setLayoutHistory({ future: [], past: [] });
  }, []);

  const commitDraftLayout = useCallback(
    (
      layout: DashboardLayout<WidgetType>,
      previousLayout = draftLayoutRef.current,
    ) => {
      if (dashboardLayoutsEqual(layout, previousLayout)) {
        replaceDraftLayout(layout);
        return;
      }

      setLayoutHistory((currentHistory) => ({
        future: [],
        past: [...currentHistory.past, copyDashboardLayout(previousLayout)],
      }));
      replaceDraftLayout(layout);
    },
    [replaceDraftLayout],
  );

  useEffect(() => {
    const layout = storageKey
      ? readDashboardLayout(storageKey, defaultLayout, isValidLayout)
      : defaultLayout;
    setSavedLayout(copyDashboardLayout(layout));
    replaceDraftLayout(layout);
    clearHistory();
  }, [
    clearHistory,
    defaultLayout,
    isValidLayout,
    replaceDraftLayout,
    storageKey,
  ]);

  const undo = useCallback(() => {
    const previousLayout = layoutHistory.past[layoutHistory.past.length - 1];
    if (!previousLayout) {
      return;
    }

    const currentLayout = copyDashboardLayout(draftLayoutRef.current);
    replaceDraftLayout(previousLayout);
    setLayoutHistory({
      future: [currentLayout, ...layoutHistory.future],
      past: layoutHistory.past.slice(0, -1),
    });
  }, [layoutHistory, replaceDraftLayout]);

  const redo = useCallback(() => {
    const nextLayout = layoutHistory.future[0];
    if (!nextLayout) {
      return;
    }

    const currentLayout = copyDashboardLayout(draftLayoutRef.current);
    replaceDraftLayout(nextLayout);
    setLayoutHistory({
      future: layoutHistory.future.slice(1),
      past: [...layoutHistory.past, currentLayout],
    });
  }, [layoutHistory, replaceDraftLayout]);

  const save = useCallback(() => {
    if (storageKey) {
      writeDashboardLayout(storageKey, draftLayout);
    }
    setSavedLayout(copyDashboardLayout(draftLayout));
    clearHistory();
  }, [clearHistory, draftLayout, storageKey]);

  const discard = useCallback(() => {
    replaceDraftLayout(savedLayout);
    clearHistory();
  }, [clearHistory, replaceDraftLayout, savedLayout]);

  const reset = useCallback(() => {
    commitDraftLayout(defaultLayout);
  }, [commitDraftLayout, defaultLayout]);

  return {
    clearHistory,
    commitDraftLayout,
    discard,
    draftLayout,
    draftLayoutRef,
    hasUnsavedChanges: !dashboardLayoutsEqual(draftLayout, savedLayout),
    layoutHistory,
    redo,
    replaceDraftLayout,
    reset,
    save,
    savedLayout,
    undo,
  };
}
